/* eslint-disable jira/naming/disallow-filenames */
import { softwareGoalsEntrypoint } from '@jira/spa-apps__goals/src/softwareGoalsEntrypoint.entrypoint.tsx';
import type { TenantContext } from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import type {
	JSResourceReference,
	EntryPointComponent,
	PreloadProps,
	EntryPoint,
} from 'react-relay';
import type { OperationType } from 'relay-runtime';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { gsnSidebarContentEntryPoint } from '@jira/nav-platform-integration__sidebar/src/gsnSidebarContentEntryPoint.entrypoint.tsx';
import { jiraHorizontalNavEntrypoint } from '@jira/navigation-apps__horizontal-nav-jira/src/jiraHorizontalNavEntrypoint.entrypoint.tsx';
import { nav4SidebarContentCoreEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/nav4SidebarContentCoreEntryPoint.entrypoint.tsx';
import { nav4ProjectsContentEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-core/src/ui/projects/default/async-content/nav4ProjectsContentEntryPoint.entrypoint.tsx';
import { nav4JSWProjectEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-projects-jsw-project-menu/src/ui/jsw-expandable-project/async-content/nav4JSWProjectEntryPoint.entrypoint.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { AnyEntryPoint } from '@jira/platform__entry-point/src/common/types.tsx';
import type { JiraSpaPageContainerQuery } from '@jira/platform__page-container-v2/src/common/ui/page-container/__generated__/JiraSpaPageContainerQuery.graphql';
import type { JiraSpaPageContainerEntryEntrypointProps } from '@jira/platform__page-container-v2/src/common/ui/page-container/JiraSpaPageContainerEntry.entrypoint.types.tsx';
import type { RouterContext } from '@atlassian/react-resource-router';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';

export const softwareGoalsClassicPageEntrypoint: {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	};
	toString: () => string;
} = createPageEntryPoint({
	main: softwareGoalsEntrypoint,
	leftSidebar: {
		sidebar: nav4SidebarContentCoreEntryPoint,
		entryPoints: {
			projects: { entryPoint: nav4ProjectsContentEntryPoint },
			boards: { entryPoint: nav4JSWProjectEntryPoint },
		},
		isProjectsExpanded: true,
	},
	horizontalNavigation: jiraHorizontalNavEntrypoint,
});

export const GSNSoftwareGoalsClassicPageEntrypoint: {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
	getPreloadProps: (entryPointParams: EntryPointRouteParams) => {
		entryPoints: {
			___pageLayoutRedirect?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutRightSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutHorizontalNavigation?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							fallback: (() => JSX.Element) | undefined;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutPreviewPanel?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: EntryPointRouteParams;
				  }
				| undefined;
			__pageLayoutLeftSidebar?:
				| {
						entryPoint: AnyEntryPoint;
						entryPointParams: {
							entryPoints: {
								[x: string]: {
									entryPoint: AnyEntryPoint;
									entryPointParams: EntryPointRouteParams;
								};
							};
							isAppsExpanded: boolean;
							isFiltersExpanded: boolean;
							isPlansExpanded: boolean;
							isProjectsExpanded: boolean;
							isOperationsExpanded: boolean;
							context: RouterContext;
							tenantContext: TenantContext;
						};
				  }
				| undefined;
			__pageLayoutMainContent: {
				entryPoint: Readonly<{
					getPreloadProps: (
						entryPointParams: EntryPointRouteParams, // oxlint-disable-next-line @typescript-eslint/no-explicit-any
					) => PreloadProps<EntryPointRouteParams, any, any, any>;
					// oxlint-disable-next-line @typescript-eslint/no-explicit-any
					root: JSResourceReference<EntryPointComponent<any, any, any, any> | null>;
				}>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutTopNavigation: {
				entryPoint: EntryPoint<
					EntryPointComponent<
						Record<string, OperationType>,
						// oxlint-disable-next-line @typescript-eslint/no-explicit-any
						Record<string, EntryPoint<any, any> | undefined>,
						{},
						{} | null
					>,
					{}
				>;
				entryPointParams: EntryPointRouteParams;
			};
			__pageLayoutSpaContent?:
				| {
						entryPoint: EntryPoint<
							EntryPointComponent<
								{
									queryReference: JiraSpaPageContainerQuery;
								},
								{},
								JiraSpaPageContainerEntryEntrypointProps,
								{}
							>,
							EntryPointRouteParams
						>;
						entryPointParams: EntryPointRouteParams;
						options: {
							fetchPolicy: 'store-or-network';
						};
				  }
				| undefined;
		};
	};
	toString: () => string;
} = createPageEntryPoint({
	main: softwareGoalsEntrypoint,
	leftSidebar: {
		sidebar: gsnSidebarContentEntryPoint,
		entryPoints: {
			projects: { entryPoint: nav4ProjectsContentEntryPoint },
			boards: { entryPoint: nav4JSWProjectEntryPoint },
		},
		isProjectsExpanded: true,
	},
	horizontalNavigation: jiraHorizontalNavEntrypoint,
});
